/**
 * @file        src/modals/FlightDetailsModal.jsx
 * @description Read-only flight + passenger + baggage + price-summary card.
 *              Ported from `#flightDetailsModal` (~line 2230) in the design
 *              HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-15 Claude — Baggage lists PER PAX TYPE when the fare quotes more
 *                          than one; it printed the ADULT allowance for everyone.
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-22 Claude — Initial port. Currently shows the design's
 *                         hard-coded sample (matches the source 1:1); a
 *                         later pass should bind this to booking-store
 *                         state (paxName, flight, fare price breakdown).
 *   - 2026-07-03 Claude — Bound to LIVE booking-store state: every selected
 *                         leg (round trip / multi-city), ALL travellers with
 *                         their types (was 1 hardcoded adult), real fare
 *                         baggage, and the actual price breakdown incl.
 *                         add-ons (was the static ₹8,567 design sample).
 */
import { useEffect } from 'react';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';

const paxTypeLabel = (ty) =>
  ({ ADULT: 'Adult', CHILD: 'Child', INFANT: 'Infant' }[String(ty || 'ADULT').toUpperCase()] || 'Adult');

// "2026-07-05T08:45" → "Sat, 5 Jul"
const fmtDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Sum every chosen excess-baggage tier (same shape the payment page uses).
function baggageTotal(sel) {
  let t = 0;
  for (const seg of Object.values(sel || {})) {
    for (const pick of Object.values(seg || {})) t += Number(pick?.amount) || 0;
  }
  return t;
}

export default function FlightDetailsModal({ open, onClose }) {
  const flight = useBooking((s) => s.flight);
  const selectedLegs = useBooking((s) => s.selectedLegs);
  const selectedTrip = useBooking((s) => s.selectedTrip);
  const selectedFare = useBooking((s) => s.selectedFare);
  const fare = useBooking((s) => s.fare);
  const travellers = useBooking((s) => s.travellers);
  const guest = useBooking((s) => s.guest);
  const paxName = useBooking((s) => s.paxName);
  const userEmail = useBooking((s) => s.userEmail);
  const seat = useBooking((s) => s.seat);
  const seatPrice = useBooking((s) => s.seatPrice);
  const baggageSel = useBooking((s) => s.baggageSel);
  const insurance = useBooking((s) => s.insurance);

  const emailDisplay = guest?.email || userEmail || '—';
  const phoneDisplay = guest?.phone || '—';
  const contactName = guest?.fullName || [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') || paxName || '—';

  // Every ticketed traveller (1 Adult + 1 Child etc.) — never just the contact.
  const paxList = (travellers && travellers.length
    ? travellers
    : [{ firstName: contactName !== '—' ? contactName : 'Guest', lastName: '', type: 'ADULT' }]);
  const paxCounts = paxList.reduce((m, p) => {
    const k = paxTypeLabel(p.type).toLowerCase();
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const paxBreakdown = ['adult', 'child', 'infant']
    .filter((k) => paxCounts[k])
    .map((k) => `${paxCounts[k]} ${k}${paxCounts[k] > 1 ? (k === 'child' ? 'ren' : 's') : ''}`)
    .join(' + ') || '1 adult';

  // Legs: round trip / multi-city carry every chosen leg; one-way falls back to
  // the single flight card.
  const legs = (selectedLegs && selectedLegs.length ? selectedLegs : [flight]).filter(Boolean);
  const isRound = (selectedFare?.name || fare?.name) === 'Round trip';
  const legLabel = (i) =>
    legs.length === 1 ? 'Depart'
      : isRound ? (['Depart', 'Return'][i] || `Flight ${i + 1}`)
        : `Flight ${i + 1}`;

  // Same price math as the review / payment pages — one source of truth.
  const tickets = (fare && fare.price) || 0;
  const seatP = seatPrice || 0;
  const bags = baggageTotal(baggageSel);
  // Indicative — the server re-prices insurance at checkout and charges that.
  const insP = insurance?.premium || 0;
  const grand = tickets + seatP + bags + insP;
  const base = Number(selectedTrip?.baseFare) || 0;
  const taxes = Number(selectedTrip?.taxes) || 0;

  const bagInfo = legs[0]?.baggage || selectedTrip?.baggage || null;
  // Per-PAX-TYPE allowance (api/flights.js baggageByPaxOf). Listed when the fare
  // quotes more than one type — an infant's checked allowance is routinely 0 Kg
  // where the adult's is 15, and this panel was printing the adult's for all of
  // them. One type (or an older cached payload) keeps the two lines below.
  const bagPax = (legs[0]?.baggageByPax || selectedTrip?.baggageByPax || []).filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel p-6 no-scrollbar">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>
        <h2 className="font-display text-xl font-extrabold mb-5">View Details</h2>

        {/* itinerary — one block per selected leg */}
        <div className="space-y-3">
          {legs.map((fl, i) => (
            <div key={i} className="rounded-2xl border border-line bg-night/40 p-4">
              <p className="text-neon text-sm font-semibold mb-3">
                {legLabel(i)}{fmtDate(fl?.depISO) ? ` · ${fmtDate(fl.depISO)}` : ''}{fl?.dur ? ` · ${fl.dur}` : ''}
              </p>
              <div className="flex items-start gap-3">
                <div className="text-center">
                  <p className="font-display text-xl font-extrabold">{fl?.dep || '—'}</p>
                  <p className="text-muted text-xs">{fl?.from || fl?.fromCode || ''}</p>
                </div>
                <div className="flex-1 pt-1.5">
                  <div className="h-px bg-line" />
                  <p className="text-[11px] text-muted text-center mt-1">
                    {fl?.airline || ''} {fl?.code || ''} · {fl?.cabinName || 'Economy'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-display text-xl font-extrabold">{fl?.arr || '—'}{fl?.next ? <sup className="text-neon text-xs">+1</sup> : null}</p>
                  <p className="text-muted text-xs">{fl?.to || fl?.toCode || ''}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <h3 className="font-display font-bold mt-5 mb-3">Passenger &amp; contact info</h3>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border border-line bg-night/40 p-3">
            <p className="text-muted text-xs mb-1">Passenger{paxList.length > 1 ? `s (${paxList.length})` : ''}</p>
            {paxList.map((p, i) => (
              <p key={i} className="font-semibold">
                {[p.title, p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                <span className="text-muted font-normal"> ({paxTypeLabel(p.type)})</span>
              </p>
            ))}
          </div>
          <div className="rounded-xl border border-line bg-night/40 p-3">
            <p className="text-muted text-xs mb-1">Contact Info</p>
            <p className="font-semibold">{contactName}</p>
            <p className="text-muted text-xs">{[phoneDisplay, emailDisplay].filter((x) => x && x !== '—').join(' · ') || '—'}</p>
          </div>
        </div>

        <h3 className="font-display font-bold mt-5 mb-3">Baggage info</h3>
        <div className="text-sm text-muted space-y-1.5">
          {/* Supplier value or NA — never an invented allowance. A fabricated
              "15 Kgs" is only discovered at the check-in desk. */}
          {bagPax.length > 1 ? bagPax.map((p) => (
            <p key={p.type} className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M9 2h6v3h3v17H6V5h3z" /></svg>
              {p.label}: {p.cabin || 'NA'} cabin · {p.checkIn || 'NA'} check-in
            </p>
          )) : (
            <>
              <p className="flex items-center gap-2"><svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M9 2h6v3h3v17H6V5h3z" /></svg>Cabin baggage: {bagInfo?.cabin || 'NA'}</p>
              <p className="flex items-center gap-2"><svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M8 2h8v4h2v16H6V6h2z" /></svg>Check-in baggage: {bagInfo?.checkIn || 'NA'}</p>
            </>
          )}
          {bags > 0 && (
            <p className="flex items-center gap-2"><svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M6 2h12v6H6z" /></svg>Extra baggage: {inr(bags)}</p>
          )}
        </div>

        <div className="rounded-xl border border-line bg-night/40 p-4 mt-5">
          <p className="font-display font-bold text-neon mb-2">Total: {inr(grand)}</p>
          <div className="text-sm text-muted space-y-1">
            <div className="flex justify-between"><span>Tickets ({paxBreakdown})</span><span>{inr(tickets)}</span></div>
            {base > 0 && <div className="flex justify-between"><span>Fare</span><span>{inr(base)}</span></div>}
            {taxes > 0 && <div className="flex justify-between"><span>Taxes &amp; fees</span><span>{inr(taxes)}</span></div>}
            {seatP > 0 && <div className="flex justify-between"><span>Seat {seat}</span><span>{inr(seatP)}</span></div>}
            {bags > 0 && <div className="flex justify-between"><span>Extra baggage</span><span>{inr(bags)}</span></div>}
            {insP > 0 && <div className="flex justify-between"><span>Travel insurance</span><span>{inr(insP)}</span></div>}
          </div>
          <p className="text-[11px] text-muted mt-2">International transaction fees may be charged by your card issuer.</p>
        </div>
      </div>
    </div>
  );
}
